import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  createPaymentLink, 
  getAllPayments, 
  updatePaymentStatus, 
  deletePayment,
  getPaymentStats,
  updateProjectProgress,
  addProjectUpdate,
  adminLogout 
} from '../services/api';
import ProgressModal from '../components/ProgressModal';
import '../styles/AdminPayments.css';

const AdminPayments = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('create');
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    projectName: '',
    amount: '',
    description: '',
    currency: 'eur'
  });

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  useEffect(() => {
    if (activeTab === 'list') {
      fetchPayments();
      fetchStats();
    }
  }, [activeTab, filters]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const result = await getAllPayments(filters);
      if (result.success) {
        setPayments(result.payments);
      }
    } catch (err) {
      setError('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const result = await getPaymentStats();
      if (result.success) {
        setStats(result.stats);
      }
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await createPaymentLink({
        ...formData,
        amount: parseFloat(formData.amount)
      });

      if (result.success) {
        setSuccess('Lien de paiement créé avec succès !');
        setSelectedPayment(result.payment);
        setShowModal(true);
        setFormData({
          clientName: '',
          projectName: '',
          amount: '',
          description: '',
          currency: 'eur'
        });
        // Refresh list
        if (activeTab === 'list') {
          fetchPayments();
          fetchStats();
        }
      } else {
        setError(result.message || 'Erreur lors de la création');
      }
    } catch (err) {
      setError('Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(url);
    setTimeout(() => setCopiedLink(''), 2000);
  };

  const handleStatusChange = async (paymentId, newStatus) => {
    try {
      const result = await updatePaymentStatus(paymentId, newStatus);
      if (result.success) {
        fetchPayments();
        fetchStats();
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;

    try {
      const result = await deletePayment(paymentId);
      if (result.success) {
        fetchPayments();
        fetchStats();
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleUpdateProgress = async (paymentId, currentStep, stepStatus, notes = '') => {
    try {
      const result = await updateProjectProgress(paymentId, currentStep, stepStatus, notes);

      if (result.success) {
        setSelectedPayment(result.payment);
        setSuccess('Progression mise à jour !');
        fetchPayments();
        fetchStats();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const handleAddUpdate = async (paymentId, title, message) => {
    try {
      const result = await addProjectUpdate(paymentId, title, message);

      if (result.success) {
        setSelectedPayment(result.payment);
        setSuccess('Mise à jour ajoutée !');
        fetchPayments();
        setTimeout(() => setSuccess(''), 3000);
        return true;
      } else {
        setError(result.error || 'Erreur');
        return false;
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout');
      return false;
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const formatCurrency = (amount, currency = 'eur') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', className: 'status-pending' },
      paid: { label: 'Payé', className: 'status-paid' },
      cancelled: { label: 'Annulé', className: 'status-cancelled' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  return (
    <div className="admin-payments">
      {/* Header */}
      <div className="payments-header">
        <div className="header-left">
          <h1>💳 Gestion des Paiements</h1>
          <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
            ← Dashboard
          </button>
        </div>
        <button onClick={handleLogout} className="logout-btn">
          Déconnexion
        </button>
      </div>

      {/* Tabs */}
      <div className="payments-tabs">
        <button
          className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          ➕ Créer un lien
        </button>
        <button
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          📋 Liste des paiements
        </button>
      </div>

      {/* Messages */}
      {error && <div className="message error-message">{error}</div>}
      {success && <div className="message success-message">{success}</div>}

      {/* Create Tab */}
      {activeTab === 'create' && (
        <div className="create-payment-section">
          <div className="form-container">
            <h2>Créer un nouveau lien de paiement</h2>
            <p className="form-description">
              Générez un lien de paiement Stripe sécurisé pour votre client
            </p>

            <form onSubmit={handleSubmit} className="payment-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom du client *</label>
                  <input
                    type="text"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleInputChange}
                    placeholder="ex: Jean Dupont"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email du client</label>
                  <input
                    type="email"
                    name="clientEmail"
                    value={formData.clientEmail}
                    onChange={handleInputChange}
                    placeholder="ex: jean@email.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Nom du projet *</label>
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleInputChange}
                  placeholder="ex: Site E-commerce"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Montant *</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="ex: 500"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Devise</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                  >
                    <option value="eur">EUR (€)</option>
                    <option value="usd">USD ($)</option>
                    <option value="gbp">GBP (£)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description (optionnel)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description du projet ou de la facture..."
                  rows="3"
                ></textarea>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Création en cours...' : '🚀 Générer le lien de paiement'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* List Tab */}
      {activeTab === 'list' && (
        <div className="list-payment-section">
          {/* Stats */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-icon">💰</span>
                <div className="stat-info">
                  <p className="stat-value">{formatCurrency(stats.totalRevenue)}</p>
                  <p className="stat-label">Revenu total</p>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">📊</span>
                <div className="stat-info">
                  <p className="stat-value">{stats.total}</p>
                  <p className="stat-label">Total paiements</p>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">✅</span>
                <div className="stat-info">
                  <p className="stat-value">
                    {stats.byStatus.find(s => s._id === 'paid')?.count || 0}
                  </p>
                  <p className="stat-label">Payés</p>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">⏳</span>
                <div className="stat-info">
                  <p className="stat-value">
                    {stats.byStatus.find(s => s._id === 'pending')?.count || 0}
                  </p>
                  <p className="stat-label">En attente</p>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="filter-select"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="paid">Payé</option>
              <option value="cancelled">Annulé</option>
            </select>

            <input
              type="text"
              placeholder="Rechercher..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="filter-search"
            />
          </div>

          {/* Payments List */}
          {loading ? (
            <div className="loading">Chargement...</div>
          ) : payments.length === 0 ? (
            <div className="no-data">Aucun paiement trouvé</div>
          ) : (
            <div className="payments-table">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Projet</th>
                    <th>Montant</th>
                    <th>Statut</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment._id}>
                      <td>{payment.clientName}</td>
                      <td>{payment.projectName}</td>
                      <td className="amount">{formatCurrency(payment.amount, payment.currency)}</td>
                      <td>{getStatusBadge(payment.status)}</td>
                      <td>{new Date(payment.createdAt).toLocaleDateString('fr-FR')}</td>
                      <td className="actions">
                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowModal(true);
                          }}
                          className="action-btn view-btn"
                          title="Voir le lien"
                        >
                          🔗
                        </button>
                        {payment.status === 'paid' && (
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowProgressModal(true);
                            }}
                            className="action-btn progress-btn"
                            title="Gérer la progression"
                          >
                            📊
                          </button>
                        )}
                        <select
                          value={payment.status}
                          onChange={(e) => handleStatusChange(payment._id, e.target.value)}
                          className="status-select"
                        >
                          <option value="pending">En attente</option>
                          <option value="paid">Payé</option>
                          <option value="cancelled">Annulé</option>
                        </select>
                        <button
                          onClick={() => handleDelete(payment._id)}
                          className="action-btn delete-btn"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal lien de paiement */}
      {showModal && selectedPayment && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>✅ Lien de paiement généré</h2>
            
            <div className="payment-details">
              <p><strong>Client :</strong> {selectedPayment.clientName}</p>
              <p><strong>Projet :</strong> {selectedPayment.projectName}</p>
              <p><strong>Montant :</strong> {formatCurrency(selectedPayment.amount, selectedPayment.currency)}</p>
            </div>

            <div className="payment-link-box">
              <label>Lien de paiement Stripe</label>
              <div className="link-input-group">
                <input
                  type="text"
                  value={selectedPayment.paymentUrl}
                  readOnly
                  className="link-input"
                />
                <button
                  onClick={() => handleCopyLink(selectedPayment.paymentUrl)}
                  className="copy-btn"
                >
                  {copiedLink === selectedPayment.paymentUrl ? '✓ Copié' : '📋 Copier'}
                </button>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => window.open(selectedPayment.paymentUrl, '_blank')}
                className="test-link-btn"
              >
                🔗 Ouvrir le lien
              </button>
              <button onClick={() => setShowModal(false)} className="close-btn">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal progression du projet */}
      {showProgressModal && selectedPayment && (
        <ProgressModal
          payment={selectedPayment}
          onClose={() => setShowProgressModal(false)}
          onUpdateProgress={handleUpdateProgress}
          onAddUpdate={handleAddUpdate}
        />
      )}
    </div>
  );
};

export default AdminPayments;